import { useSearchParams } from 'react-router-dom';

import { playerSidebar } from '@/config/sidebarConfig.ts';
import { getMyHistoryApi } from '@/lib/api/usersApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { COMP_STATUS_CONFIG, REG_STATUS_CONFIG } from '@/lib/status-config';
import { useAuthStore } from '@/stores/auth-store';
import {
  COMPETITION_LEVEL_LABELS as LEVEL_LABELS,
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
} from '@shared';
import { CompetitionStatus, CompetitionType, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { PlayerHistoryEntry } from '@shared/types/entities';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Award,
  Calendar,
  ChevronLeft,
  ChevronRight,
  History,
  MapPin,
  Medal,
  Shield,
  Trophy,
  Users,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Constants ───────────────────────────────────────────────────
const PAGE_SIZE = 20;
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2021 }, (_, i) => CURRENT_YEAR - i);

// ─── Labels ──────────────────────────────────────────────────────
const TYPE_COLORS: Record<CompetitionType, string> = {
  [CompetitionType.SINGLE]: 'bg-violet-100 text-violet-700',
  [CompetitionType.DOUBLE]: 'bg-blue-100 text-blue-700',
  [CompetitionType.TRIPLE]: 'bg-indigo-100 text-indigo-700',
};

// ─── Rank display ────────────────────────────────────────────────
function RankBadge({ rank }: { rank: number | null }) {
  if (rank === null) return <span className='text-slate-300 text-sm'>—</span>;

  const suffix = rank === 1 ? 'er' : 'ème';

  if (rank === 1) {
    return (
      <span className='inline-flex items-center gap-1 font-extrabold text-yellow-500 text-sm'>
        <Trophy className='w-4 h-4' />1{suffix}
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className='inline-flex items-center gap-1 font-bold text-slate-400 text-sm'>
        <Medal className='w-4 h-4' />2{suffix}
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className='inline-flex items-center gap-1 font-bold text-amber-600 text-sm'>
        <Award className='w-4 h-4' />3{suffix}
      </span>
    );
  }
  return (
    <span className='font-semibold text-slate-600 text-sm'>
      {rank}
      {suffix}
    </span>
  );
}

// ─── Pagination helper ───────────────────────────────────────────

// ─── Table Row ───────────────────────────────────────────────────
function HistoryRow({ entry }: { entry: PlayerHistoryEntry }) {
  const comp = entry.competition;
  const typeCls = TYPE_COLORS[comp.type] ?? 'bg-slate-100 text-slate-600';
  const statusCfg = COMP_STATUS_CONFIG[comp.status as CompetitionStatus];
  const regCfg = REG_STATUS_CONFIG[entry.registrationStatus as TeamRegistrationStatus];

  return (
    <tr className='hover:bg-slate-50 transition-colors'>
      {/* Date */}
      <td className='px-5 py-4 text-sm text-slate-500 whitespace-nowrap'>{formatDateAbbreviatedFr(comp.date)}</td>

      {/* Concours */}
      <td className='px-5 py-4'>
        <p className='font-semibold text-slate-900 text-sm leading-snug'>{comp.name}</p>
        <div className='flex items-center gap-2 mt-1 flex-wrap'>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${typeCls}`}>
            {TYPE_LABELS[comp.type]}
          </span>
          <span className='text-xs text-slate-400'>{LEVEL_LABELS[comp.level]}</span>
          {(comp.venue || comp.city) && (
            <span className='flex items-center gap-0.5 text-xs text-slate-400'>
              <MapPin className='w-3 h-3 shrink-0' />
              {comp.venue || comp.city}
            </span>
          )}
        </div>
      </td>

      {/* Équipe + rôle */}
      <td className='px-5 py-4'>
        <p className='text-sm font-medium text-slate-800'>{entry.teamName}</p>
        <span
          className={`inline-flex items-center gap-1 text-xs font-semibold mt-0.5 ${
            entry.role === 'CAPTAIN' ? 'text-orange-600' : 'text-slate-400'
          }`}
        >
          {entry.role === 'CAPTAIN' ? (
            <>
              <Shield className='w-3 h-3' />
              Capitaine
            </>
          ) : (
            <>
              <Users className='w-3 h-3' />
              Joueur
            </>
          )}
        </span>
      </td>

      {/* Statut inscription */}
      <td className='px-5 py-4'>
        {regCfg ? (
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${regCfg.bg} ${regCfg.text}`}>
            {regCfg.label}
          </span>
        ) : (
          <span className='text-slate-300 text-xs'>—</span>
        )}
      </td>

      {/* Statut concours */}
      <td className='px-5 py-4'>
        {statusCfg ? (
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.text}`}
          >
            {statusCfg.label}
          </span>
        ) : (
          <span className='text-slate-300 text-xs'>{comp.status}</span>
        )}
      </td>

      {/* Classement */}
      <td className='px-5 py-4'>
        <RankBadge rank={entry.result?.rank ?? null} />
        {entry.result?.points != null && <p className='text-xs text-slate-400 mt-0.5'>{entry.result.points} pts</p>}
      </td>
    </tr>
  );
}

// ─── Skeleton row ────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr>
      {Array.from({ length: 6 }).map((_, i) => (
        <td key={i} className='px-5 py-4'>
          <div className='h-4 bg-slate-200 animate-pulse rounded w-full' />
        </td>
      ))}
    </tr>
  );
}

// ─── Best rank label ─────────────────────────────────────────────
function bestRankLabel(rank: number | null): string {
  if (rank === null) return '—';
  if (rank === 1) return '1er';
  return `${rank}ème`;
}

// ─── Main Page ───────────────────────────────────────────────────
export default function PlayerHistory() {
  const userId = useAuthStore(s => s.user)?.id;
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const [searchParams, setSearchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const year = yearParam ? Number(yearParam) : undefined;
  const page = parseInt(searchParams.get('page') ?? '1', 10);

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };

  const params = { ...(year !== undefined && { year }), page, limit: PAGE_SIZE };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.users.list({ history: true, userId, ...params }),
    queryFn: () => getMyHistoryApi(params),
    enabled: !!userId && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const entries = data?.data ?? [];
  const stats = data?.stats ?? { total: 0, wins: 0, podiums: 0, bestRank: null };
  const total = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  function handleYearChange(value: number | undefined) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value !== undefined) next.set('year', String(value));
        else next.delete('year');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={playerSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={playerSidebar}>
      <div className='p-8'>
        <PageHeader
          title='Historique'
          subtitle='Toutes vos participations aux concours'
          actions={
            <div className='flex items-center gap-2'>
              <Calendar className='w-4 h-4 text-slate-400' />
              <select
                value={year ?? ''}
                onChange={e => handleYearChange(e.target.value ? Number(e.target.value) : undefined)}
                className='px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-700 bg-white focus:border-orange-400 focus:outline-none'
              >
                <option value=''>Toutes les années</option>
                {YEARS.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              {isFetching && !isLoading && <span className='text-xs text-slate-400'>Chargement…</span>}
            </div>
          }
        />

        {/* ─── Stats ─── */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <StatCard
            icon={History}
            iconBg='bg-orange-100'
            iconColor='text-orange-600'
            label='Total concours'
            value={String(stats.total)}
            subtitle={year ? `En ${year}` : 'Toutes années'}
          />
          <StatCard
            icon={Trophy}
            iconBg='bg-yellow-100'
            iconColor='text-yellow-600'
            label='Victoires'
            value={String(stats.wins)}
            subtitle={stats.total > 0 ? `${Math.round((stats.wins / stats.total) * 100)} % de victoires` : '—'}
          />
          <StatCard
            icon={Medal}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Podiums'
            value={String(stats.podiums)}
            subtitle={stats.total > 0 ? `${Math.round((stats.podiums / stats.total) * 100)} % de podiums` : '—'}
          />
          <StatCard
            icon={Award}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Meilleur classement'
            value={bestRankLabel(stats.bestRank)}
            subtitle='Tous concours confondus'
          />
        </div>

        {/* ─── Table ─── */}
        <div className='bg-white rounded-2xl border border-slate-200 overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-slate-200 bg-slate-50'>
                  <th
                    scope='col'
                    className='text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap'
                  >
                    Date
                  </th>
                  <th
                    scope='col'
                    className='text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Concours
                  </th>
                  <th
                    scope='col'
                    className='text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Équipe
                  </th>
                  <th
                    scope='col'
                    className='text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap'
                  >
                    Inscription
                  </th>
                  <th
                    scope='col'
                    className='text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Statut
                  </th>
                  <th
                    scope='col'
                    className='text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Classement
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                ) : entries.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='px-5 py-16 text-center text-sm text-slate-400'>
                      <div className='flex flex-col items-center gap-3'>
                        <History className='w-12 h-12 opacity-20' />
                        <p>Aucune participation{year ? ` en ${year}` : ''}</p>
                        {year && (
                          <button
                            onClick={() => handleYearChange(undefined)}
                            className='text-xs text-orange-600 hover:underline'
                          >
                            Voir toutes les années
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  entries.map(entry => <HistoryRow key={`${entry.teamId}-${entry.competition.id}`} entry={entry} />)
                )}
              </tbody>
            </table>
          </div>

          {/* ─── Pagination ─── */}
          {total > PAGE_SIZE && (
            <div className='flex items-center justify-between border-t border-slate-200 px-5 py-4'>
              <p className='text-sm text-slate-500'>
                Affichage de <span className='font-semibold text-slate-700'>{from}</span> à{' '}
                <span className='font-semibold text-slate-700'>{to}</span> sur{' '}
                <span className='font-semibold text-slate-700'>{total.toLocaleString('fr-FR')}</span> participations
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                    disabled={page === 1}
                    aria-label='Page précédente'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  {getPageNumbers(page, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-2 text-slate-400'>
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setParam('page', String(p))}
                        aria-current={p === page ? 'page' : undefined}
                        aria-label={`Page ${p}`}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-orange-500 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
                    disabled={page >= totalPages}
                    aria-label='Page suivante'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
